// Projects (SPEC F7, §8 Project): the beat being made. The `projects` table has had every index this needs
// since schema v1 (`&id, styleId, updatedAt, archived, deletedAt`); the key fields are optional and
// unindexed, so there is no Dexie bump (DECISIONS 2026-10-08). Delete is soft, with undo, purged after 24 h
// (DESIGN_SYSTEM §5). Writes that touch more than one table run in one transaction.
//
// `Settings.activeProjectId` is the store's job (src/app/store.ts); this file is the data layer only.

import { styleName } from '../../data/styles';
import type { Kit, PadSettings, Pattern, Project, StyleId } from '../../data/types';
import { WORKING_ID, freshPattern, switchStyle } from '../../lib/grooveModel';
import { getDb } from '../schema';
import { normalizePads } from './kits';
import { UNDO_WINDOW_MS } from './marks';

export { UNDO_WINDOW_MS };

const newId = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `p${Date.now()}${Math.random().toString(36).slice(2, 8)}`);

/** The project's own copy of the groove lives under this id (SPEC F7, DECISIONS 2026-10-06). */
export const projectPatternId = (projectId: string): string => `proj-${projectId}`;

/** "Soul boom bap beat 3": the style name, "beat", and the next free number. */
export function autoName(styleLabel: string, existing: readonly string[]): string {
  const prefix = `${styleLabel} beat `;
  let highest = 0;
  for (const name of existing) {
    if (!name.startsWith(prefix)) continue;
    const n = Number(name.slice(prefix.length));
    if (Number.isInteger(n) && n > highest) highest = n;
  }
  return `${prefix}${highest + 1}`;
}

export interface NewProject {
  styleId: StyleId;
  bpm: number;
  /** Defaults to the auto name. */
  name?: string;
}

/** A live (not deleted) project. */
export async function getProject(id: string): Promise<Project | undefined> {
  const p = await getDb().projects.get(id);
  return p && p.deletedAt === undefined ? p : undefined;
}

/** Live projects, most recently changed first. */
export async function listProjects(filter: { archived?: boolean } = {}): Promise<Project[]> {
  const all = await getDb().projects.orderBy('updatedAt').reverse().toArray();
  return all.filter((p) => p.deletedAt === undefined && (filter.archived === undefined || p.archived === filter.archived));
}

/**
 * Creates a project (no key, no steps checked). With `kitPads` it also creates the project's kit, linked, in
 * the same transaction ("Defaults do the work", DESIGN_SYSTEM §5); without them no kit is made, so no empty
 * kit is left behind.
 */
export async function createProject(input: NewProject, now: number, kitPads?: readonly Partial<PadSettings>[]): Promise<Project> {
  const db = getDb();
  return db.transaction('rw', db.projects, db.kits, async () => {
    // Every row counts, archived and soft-deleted too, so an Undo never makes two with one name
    const names = (await db.projects.toArray()).map((p) => p.name);
    const project: Project = {
      id: newId(), name: input.name?.trim() || autoName(styleName(input.styleId), names), styleId: input.styleId, bpm: input.bpm,
      markIds: [], checks: {}, notes: '', step: 0, archived: false, createdAt: now, updatedAt: now,
    };
    if (kitPads) {
      const kit: Kit = { id: newId(), name: `${project.name} kit`, pads: normalizePads(kitPads), styleId: project.styleId, projectId: project.id, updatedAt: now };
      await db.kits.put(kit);
      project.kitId = kit.id;
    }
    await db.projects.put(project);
    return project;
  });
}

type ProjectPatch = Partial<Pick<Project, 'name' | 'styleId' | 'notes'>>;

export async function updateProject(id: string, patch: ProjectPatch, now: number): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.projects, async () => {
    const p = await db.projects.get(id);
    if (!p) return;
    const next: Project = { ...p, ...patch, updatedAt: now };
    if (patch.name !== undefined) {
      const name = patch.name.trim();
      if (!name) throw new RangeError('A project needs a name');
      next.name = name;
    }
    await db.projects.put(next);
  });
}

/** Sets or clears the key together (a root without a quality means nothing). */
export async function setKey(id: string, key: { keyRoot: number; minor: boolean } | undefined, now: number): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.projects, async () => {
    const p = await db.projects.get(id);
    if (!p) return;
    const next: Project = { ...p, updatedAt: now };
    delete next.keyRoot;
    delete next.minor;
    await db.projects.put(key ? { ...next, ...key } : next);
  });
}

/** Checks or unchecks a wizard step. Checks are keyed by the step id (w01 to w14), never by position (SPEC F7). */
export async function setCheck(id: string, stepId: string, done: boolean, now: number): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.projects, async () => {
    const p = await db.projects.get(id);
    if (!p) return;
    const others = Object.entries(p.checks).filter(([id]) => id !== stepId);
    const checks = Object.fromEntries(done ? [...others, [stepId, true]] : others);
    await db.projects.put({ ...p, checks, updatedAt: now });
  });
}

/** The project's BPM, and its groove's BPM with it, together. */
export async function setBpm(id: string, bpm: number, now: number): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.projects, db.patterns, async () => {
    const p = await db.projects.get(id);
    if (!p) return;
    await db.projects.put({ ...p, bpm, updatedAt: now });
    const pattern = p.patternId ? await db.patterns.get(p.patternId) : undefined;
    if (pattern) await db.patterns.put({ ...pattern, bpm });
  });
}

/** The project's groove changed its BPM: the project follows. */
export async function syncBpmFromPattern(projectId: string, bpm: number, now: number): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.projects, async () => {
    const p = await db.projects.get(projectId);
    if (p && p.bpm !== bpm) await db.projects.put({ ...p, bpm, updatedAt: now });
  });
}

/**
 * The project's groove, copied from the working groove on the first open and never again (DECISIONS
 * 2026-10-06). The copy takes the project's style (parts you edited stay) and its BPM. Without a working
 * groove it starts from the style's pattern. One transaction, so two opens at once make one pattern.
 */
export async function openProjectPattern(projectId: string): Promise<Pattern | undefined> {
  const db = getDb();
  return db.transaction('rw', db.projects, db.patterns, async () => {
    const project = await db.projects.get(projectId);
    if (!project || project.deletedAt !== undefined) return undefined;
    const existing = project.patternId ? await db.patterns.get(project.patternId) : undefined;
    if (existing && existing.deletedAt === undefined) return existing;
    const working = await db.patterns.get(WORKING_ID);
    const source: Pattern = working && working.deletedAt === undefined ? { ...working, laneToPad: working.laneToPad ?? {} } : freshPattern(project.styleId);
    const copy: Pattern = { ...switchStyle(source, project.styleId), id: projectPatternId(projectId), projectId, bpm: project.bpm };
    await db.patterns.put(copy);
    await db.projects.put({ ...project, patternId: copy.id });
    return copy;
  });
}

/** A copy named "<name> copy", with its own kit and groove (marks stay with the original: a mark has one project). Starts unarchived. */
export async function duplicateProject(id: string, now: number): Promise<Project | undefined> {
  const db = getDb();
  return db.transaction('rw', db.projects, db.kits, db.patterns, async () => {
    const source = await db.projects.get(id);
    if (!source || source.deletedAt !== undefined) return undefined;
    const { kitId, patternId } = source;
    const copy: Project = { ...source, id: newId(), name: `${source.name} copy`, markIds: [], checks: { ...source.checks }, archived: false, createdAt: now, updatedAt: now };
    delete copy.kitId;
    delete copy.patternId;
    delete copy.deletedAt;
    // The copy is a new beat: it is not finished, and the journal's sessions stay with the original
    delete copy.finishedAt;
    const kit = kitId ? await db.kits.get(kitId) : undefined;
    if (kit && kit.deletedAt === undefined) {
      const kitCopy: Kit = { id: newId(), name: `${kit.name} copy`, pads: normalizePads(kit.pads), projectId: copy.id, updatedAt: now };
      if (kit.styleId) kitCopy.styleId = kit.styleId;
      await db.kits.put(kitCopy);
      copy.kitId = kitCopy.id;
    }
    const pattern = patternId ? await db.patterns.get(patternId) : undefined;
    if (pattern && pattern.deletedAt === undefined) {
      const patternCopy: Pattern = { ...pattern, id: projectPatternId(copy.id), projectId: copy.id };
      await db.patterns.put(patternCopy);
      copy.patternId = patternCopy.id;
    }
    await db.projects.put(copy);
    return copy;
  });
}

/**
 * Marks a beat finished by hand (SPEC F10), without a check-out: `finishedAt` is the end of its latest ended
 * session, or `now` when it has none. Writes no session outcome and leaves `updatedAt` alone, like the check-out's
 * "done". Returns the previous value (`null` when there was none) for Undo; `undefined` when the project is gone.
 */
export async function markFinished(id: string, now: number): Promise<number | null | undefined> {
  const db = getDb();
  return db.transaction('rw', db.projects, db.sessions, async () => {
    const p = await db.projects.get(id);
    if (!p || p.deletedAt !== undefined) return undefined;
    const ends = (await db.sessions.where('projectId').equals(id).toArray()).flatMap((s) => (s.endedAt !== undefined ? [s.endedAt] : []));
    await db.projects.put({ ...p, finishedAt: ends.length > 0 ? Math.max(...ends) : now });
    return p.finishedAt ?? null;
  });
}

/** Sets `finishedAt` to a value (Undo of mark and unmark) or clears it with `null`. Returns the previous value. */
export async function setFinishedAt(id: string, finishedAt: number | null): Promise<number | null | undefined> {
  const db = getDb();
  return db.transaction('rw', db.projects, async () => {
    const p = await db.projects.get(id);
    if (!p || p.deletedAt !== undefined) return undefined;
    const next: Project = { ...p };
    if (finishedAt === null) delete next.finishedAt;
    else next.finishedAt = finishedAt;
    await db.projects.put(next);
    return p.finishedAt ?? null;
  });
}

export async function archiveProject(id: string, archived: boolean, now: number): Promise<void> {
  await getDb().projects.update(id, { archived, updatedAt: now });
}

export async function softDeleteProject(id: string, now: number): Promise<void> {
  await getDb().projects.update(id, { deletedAt: now });
}

export async function restoreProject(id: string): Promise<void> {
  await getDb().projects.update(id, { deletedAt: undefined });
}

/**
 * Deleted projects past the undo window are gone for good, with their groove. Their kit and marks stay
 * (they are yours) but no longer point at the project.
 */
export async function purgeExpiredProjects(now: number): Promise<number> {
  const db = getDb();
  return db.transaction('rw', db.projects, db.kits, db.patterns, db.marks, db.sessions, async () => {
    const dead = (await db.projects.toArray()).filter((p) => p.deletedAt !== undefined && now - p.deletedAt > UNDO_WINDOW_MS);
    if (dead.length === 0) return 0;
    const ids = new Set(dead.map((p) => p.id));
    for (const k of await db.kits.toArray()) if (k.projectId && ids.has(k.projectId)) await db.kits.update(k.id, { projectId: undefined });
    for (const m of await db.marks.toArray()) if (m.projectId && ids.has(m.projectId)) await db.marks.update(m.id, { projectId: undefined });
    await db.patterns.bulkDelete(dead.flatMap((p) => (p.patternId ? [p.patternId] : [])));
    // The journal belongs to the project: its sessions go with it (SPEC F10, Step D's export carries them)
    for (const id of ids) await db.sessions.where('projectId').equals(id).delete();
    await db.projects.bulkDelete([...ids]);
    return dead.length;
  });
}

/**
 * Makes the project's kit when its first pad is filled or saved (Kit screen, "for <project>"), linked, in one
 * write. The Kit screen shows a draft until then, so no empty kit is ever left behind (DECISIONS 2026-10-08).
 * A project with a kit already gets that kit back, unchanged.
 */
export async function createProjectKit(projectId: string, pads: readonly Partial<PadSettings>[], now: number): Promise<Kit | undefined> {
  const db = getDb();
  return db.transaction('rw', db.projects, db.kits, async () => {
    const project = await db.projects.get(projectId);
    if (!project || project.deletedAt !== undefined) return undefined;
    const existing = project.kitId ? await db.kits.get(project.kitId) : undefined;
    if (existing && existing.deletedAt === undefined) return existing;
    const kit: Kit = { id: newId(), name: `${project.name} kit`, pads: normalizePads(pads), styleId: project.styleId, projectId, updatedAt: now };
    await db.kits.put(kit);
    await db.projects.put({ ...project, kitId: kit.id, updatedAt: now });
    return kit;
  });
}

/** The name the next new project in `styleId` would get, for the New project sheet. */
export async function previewProjectName(styleId: StyleId): Promise<string> {
  const names = (await getDb().projects.toArray()).map((p) => p.name);
  return autoName(styleName(styleId), names);
}

/** The project's own groove, if it has been opened once. */
export async function getProjectPattern(project: Pick<Project, 'patternId'>): Promise<Pattern | undefined> {
  const row = project.patternId ? await getDb().patterns.get(project.patternId) : undefined;
  return row && row.deletedAt === undefined ? row : undefined;
}
